import { useState } from "react";
import { usePublicConfig } from "@/lib/publicConfig";
import { sellerNet } from "@/lib/sellerNet";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { X } from "lucide-react";

const $ = (n) => `$${Number(n || 0).toFixed(2)}`;

const QueueRow = ({ d, i, active, minPhotos, isComplete, onOpen, onRemove, onRetry, loading }) => {
  const img = d.selected?.image_url;
  const noPhotos = d.photos.length + (d.photo_paths?.length || 0) < minPhotos;
  return (
    <li className={`flex items-center gap-3 p-2.5 rounded-[14px] border ${active ? "border-[#FF4438]" : "border-[#262626]"}`} data-testid={`queue-item-${i}`}>
      <div className="w-10 h-14 shrink-0 rounded bg-black overflow-hidden grid place-items-center">
        {img ? <img src={img.startsWith("http") ? img : `${process.env.REACT_APP_BACKEND_URL}/api/files/public/${img}`} alt="" className="w-full h-full object-contain" /> : null}
      </div>
      <button type="button" onClick={() => onOpen(i)} className="flex-1 min-w-0 text-left">
        <div className="text-sm text-white truncate">{d.selected?.name}</div>
        <div className="text-xs text-neutral-400">{d.form.price ? `${$(d.form.price)} × ${d.form.quantity || 1}` : "Falta precio"}{d.form.version_label ? ` · ${d.form.version_label}` : ""}{d.form.first_edition ? " · 1.ª EDICIÓN" : ""}</div>
        {d.error && <div className="text-[11px] text-[#FF6B5E] mt-0.5" data-testid={`queue-item-error-${i}`}>No se pudo publicar: {d.error}</div>}
      </button>
      {d.error && !noPhotos && isComplete(d) && (
        <button type="button" disabled={loading} onClick={() => onRetry(i)} className="h-8 px-3 rounded-full bg-[#FF4438] text-white text-xs font-semibold" data-testid={`queue-item-retry-${i}`}>Reintentar</button>
      )}
      {noPhotos ? (
        <button type="button" onClick={() => onOpen(i, "photos")} className="h-8 px-3 rounded-full border border-amber-500/60 text-amber-300 text-xs font-semibold" data-testid={`queue-item-photos-${i}`}>+ Fotos</button>
      ) : isComplete(d) && !d.error ? <span className="text-[10px] uppercase tracking-wider text-emerald-400" data-testid={`queue-item-ready-${i}`}>Lista</span> : null}
      <button type="button" onClick={() => onRemove(i)} className="text-neutral-600 hover:text-[#FF4438]" data-testid={`queue-item-remove-${i}`}><X size={14} /></button>
    </li>
  );
};

// P4 · Cola — hoja inferior con la lista; "Recibes" sobre el total (mínimo UNA vez por vendedor).
export const QueueSheet = ({ drafts, activeTab, minPhotos, isComplete, onOpen, onRemove, onPublish, onRetry, loading, inline = false }) => {
  const [open, setOpen] = useState(false);
  const cfg = usePublicConfig();
  const items = drafts.map((d, i) => ({ d, i })).filter(({ d }) => d.selected);
  if (!inline && items.length < 2 && drafts.length < 2) return null;
  const lines = items.filter(({ d }) => parseFloat(d.form.price) > 0).map(({ d }) => ({ price: parseFloat(d.form.price), quantity: d.form.quantity || 1 }));
  const listed = lines.reduce((a, l) => a + l.price * (parseInt(l.quantity, 10) || 1), 0);
  const tr = lines.length && cfg.commission_tiers ? sellerNet(lines, 0, "transfer", cfg, "cart").net : 0;
  const cd = lines.length && cfg.commission_tiers ? sellerNet(lines, 0, "card", cfg, "cart").net : 0;
  const ready = drafts.filter((d) => isComplete(d) && !d.error).length;
  const go = (i, where) => { setOpen(false); onOpen(i, where); };
  const body = (
    <>
      <ul className="mt-3 space-y-2">
        {items.map(({ d, i }) => <QueueRow key={d.id} d={d} i={i} active={i === activeTab} minPhotos={minPhotos} isComplete={isComplete} onOpen={go} onRemove={onRemove} onRetry={onRetry} loading={loading} />)}
      </ul>
      <div className="mt-4 pt-3 border-t border-[#262626] space-y-1 text-sm">
        <div className="flex justify-between"><span className="text-neutral-400">Valor listado</span><span data-testid="queue-listed">{$(listed)}</span></div>
        <div className="flex justify-between"><span className="text-neutral-400">Recibes · transferencia</span><span className="text-emerald-400 font-semibold" data-testid="queue-net-transfer">{$(tr)} + envío</span></div>
        <div className="flex justify-between"><span className="text-neutral-400">Recibes · tarjeta</span><span data-testid="queue-net-card">{$(cd)} + envío</span></div>
      </div>
      <button type="button" disabled={!ready || loading} onClick={() => { setOpen(false); onPublish(); }} data-testid="queue-publish"
        className="mt-4 w-full h-12 rounded-full bg-[#FF4438] text-white font-bold disabled:opacity-40">
        {loading ? "Publicando…" : `Publicar ${ready} lista${ready === 1 ? "" : "s"}`}
      </button>
    </>
  );
  if (inline) {
    return (
      <aside className="sticky top-24 bg-[#111] border border-[#262626] rounded-md p-4" data-testid="queue-inline">
        <div className="text-sm font-semibold text-white">{items.length} en cola</div>
        {body}
      </aside>
    );
  }
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="queue-open"
        className="fixed bottom-4 right-4 z-30 h-12 px-5 rounded-full bg-white text-black font-bold text-sm shadow-lg shadow-black/50">
        Cola · {items.length}{ready ? ` · ${ready} lista${ready === 1 ? "" : "s"}` : ""}
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="bg-[#0A0A0A] border-[#262626] text-white max-h-[85vh] overflow-y-auto rounded-t-[20px]" data-testid="queue-sheet">
          <SheetHeader><SheetTitle className="text-white text-left">Tu cola · {items.length}</SheetTitle></SheetHeader>
          {body}
        </SheetContent>
      </Sheet>
    </>
  );
};
